<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { supabase } from '@/supabase';
import { trimestres } from '@/store/dadosPadrao';
import Tag from 'primevue/tag';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const props = defineProps({
    data: Object
})

const store = useStore();

const carregandoNotasAno = ref(false);
const avaliacoesAnoLetivo = ref([]);

// Evita refazer as 3 primeiras buscas (disciplina/ano letivo/planos) a cada
// aluno clicado dentro da mesma turma - só a busca das notas (passo 4) muda.
const planoIdsCache = ref(null);
const idDaAulaResolvida = ref(null);

async function resolverPlanosDaDisciplinaNoAno(idDaAula) {
    const { data: aulaAtual, error: erroAula } = await supabase
        .from('aulas')
        .select('curso, cursos(disciplina, anoLetivo, anoDoEnsinoMedio)')
        .eq('id', idDaAula)
        .single()

    if (erroAula || !aulaAtual?.cursos) {
        throw new Error('Não foi possível identificar a disciplina/ano letivo da turma.')
    }

    const { disciplina, anoLetivo, anoDoEnsinoMedio } = aulaAtual.cursos

    const { data: cursosRelacionados, error: erroCursos } = await supabase
        .from('cursos')
        .select('aulas(id)')
        .eq('disciplina', disciplina)
        .eq('anoLetivo', anoLetivo)
        .eq('anoDoEnsinoMedio', anoDoEnsinoMedio)

    if (erroCursos) throw new Error(erroCursos.message)

    const aulaIds = cursosRelacionados.flatMap(curso => curso.aulas.map(aula => aula.id))
    if (aulaIds.length === 0) return []

    const { data: planosAulas, error: erroPlanosAulas } = await supabase
        .from('planos_aulas')
        .select('plano')
        .in('aula', aulaIds)

    if (erroPlanosAulas) throw new Error(erroPlanosAulas.message)

    return [...new Set(planosAulas.map(planoAula => planoAula.plano))]
}

async function obterPlanoIds() {
    const idDaAula = store.state.turma.idDaAula

    if (planoIdsCache.value !== null && idDaAulaResolvida.value === idDaAula) {
        return planoIdsCache.value
    }

    const planoIds = await resolverPlanosDaDisciplinaNoAno(idDaAula)
    planoIdsCache.value = planoIds
    idDaAulaResolvida.value = idDaAula
    return planoIds
}

async function buscarNotasDoAlunoNoAno(estudanteId) {
    if (!estudanteId) return

    carregandoNotasAno.value = true
    avaliacoesAnoLetivo.value = []

    try {
        const planoIds = await obterPlanoIds()

        if (planoIds.length === 0) {
            return
        }

        const { data, error } = await supabase
            .from('planos_avaliacoes')
            .select(`
                id, tipo, titulo, nivel1, nivel2, nivel3, nivel4,
                plano, planos(titulo, trimestre, created_at),
                criterios_rubrica(criterio),
                planos_notas!inner(id, nota, comentario, comentario_para_estudante, comentario_grupo)
            `)
            .in('plano', planoIds)
            .eq('planos_notas.estudante', estudanteId)

        if (error) throw new Error(error.message)

        avaliacoesAnoLetivo.value = data
    } catch (err) {
        alert('Ocorreu um erro ao buscar as notas do aluno: ' + err.message)
    } finally {
        carregandoNotasAno.value = false
    }
}

watch(() => props.data?.aluno?.id, (novoId) => {
    buscarNotasDoAlunoNoAno(novoId)
}, { immediate: true })

const estadosEspeciais = { 1: 'Não fez', 201: 'Dispensado', 202: 'Não avaliado' }

const rotulosNiveis = { AV: 'Excede Expectativas', AD: 'Plenamente Desenvolvido', B: 'Parcialmente Desenvolvido', AB: 'Minimamente Desenvolvido' }

const niveisRubrica = (avaliacao) => ([
    { codigo: 'AB', valor: 10, texto: avaliacao.nivel1 },
    { codigo: 'B', valor: 60, texto: avaliacao.nivel2 },
    { codigo: 'AD', valor: 90, texto: avaliacao.nivel3 },
    { codigo: 'AV', valor: 100, texto: avaliacao.nivel4 },
])

function nivelAtingido(avaliacao) {
    const nivel = avaliacao.niveis?.find(item => item.valor === avaliacao.nota)
    if (!nivel) return null
    return { ...nivel, label: rotulosNiveis[nivel.codigo] }
}

// A partir de "Plenamente Desenvolvido" (AD, nota 90) já conta como atingido.
function contagemAtingidos(plano) {
    const criteriosDeRubrica = plano.avaliacoes.filter(avaliacao => avaliacao.tipo === '1')
    const atingidos = criteriosDeRubrica.filter(avaliacao => avaliacao.nota >= 90).length
    return { atingidos, total: criteriosDeRubrica.length }
}

function nomeDoTrimestre(trimestre) {
    return trimestres.find(item => item.id === trimestre)?.trimestre ?? `Trimestre ${trimestre}`
}

const planosAgrupados = computed(() => {
    const porTrimestre = new Map();

    avaliacoesAnoLetivo.value.forEach(avaliacao => {
        const trimestre = avaliacao.planos?.trimestre ?? '-'
        const tituloPlano = avaliacao.planos?.titulo || 'Sem título'
        const criadoEm = avaliacao.planos?.created_at ?? null
        const notaDoAluno = avaliacao.planos_notas?.[0]

        if (!porTrimestre.has(trimestre)) porTrimestre.set(trimestre, new Map())
        const porPlano = porTrimestre.get(trimestre)

        if (!porPlano.has(tituloPlano)) {
            porPlano.set(tituloPlano, { titulo: tituloPlano, criadoEm, avaliacoes: [] })
        }

        porPlano.get(tituloPlano).avaliacoes.push({
            titulo: avaliacao.titulo,
            criterio: avaliacao.criterios_rubrica?.criterio ?? '',
            nota: notaDoAluno?.nota ?? 0,
            tipo: avaliacao.tipo,
            niveis: avaliacao.tipo === '1' ? niveisRubrica(avaliacao) : null
        })
    })

    // Do trimestre mais recente para o mais antigo e, dentro dele, do plano
    // mais recente (created_at) para o mais antigo.
    return [...porTrimestre.entries()]
        .sort(([trimestreA], [trimestreB]) => Number(trimestreB) - Number(trimestreA))
        .map(([trimestre, porPlano]) => ({
            trimestre,
            planos: [...porPlano.values()].sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm))
        }))
})

function defineClasses(nota) {
    if(nota == 0) return '';
    if (nota <= 10) return 'bg-red-500 text-white';
    if (nota <= 60) return 'bg-orange-500 text-white';
    if (nota <= 90) return 'bg-blue-500 text-white';
    if (nota <= 100) return 'bg-green-500 text-white';
    return '';
}
</script>

<template>
    <div class="detalhes-aluno-container mx-auto flex flex-column gap-6">
        <LoadingSpinner v-if="carregandoNotasAno" />

        <div v-else-if="planosAgrupados.length === 0" class="text-sm">
            Nenhuma nota encontrada para esta disciplina no ano letivo.
        </div>

        <div v-else v-for="grupo in planosAgrupados" :key="grupo.trimestre" class="flex flex-column gap-4">
            <div class="font-bold text-2xl">{{ nomeDoTrimestre(grupo.trimestre) }}</div>

            <Accordion :multiple="true">
                <AccordionTab v-for="plano in grupo.planos" :key="plano.titulo" :header="plano.titulo">
                    <template #header>
                        <span v-if="contagemAtingidos(plano).total > 0" class="ml-auto mr-2 text-sm font-normal text-color-secondary">
                            {{ contagemAtingidos(plano).atingidos }}/{{ contagemAtingidos(plano).total }}
                        </span>
                    </template>

                    <div v-for="(avaliacao, index) in plano.avaliacoes" :key="index" class="flex flex-column gap-1 py-2 criterio-item">
                        <div class="font-semibold text-sm">{{ avaliacao.titulo || avaliacao.criterio }}</div>

                        <div>
                            <Tag v-if="avaliacao.nota === 0" value="Ainda não avaliado" severity="secondary" />
                            <Tag v-else-if="avaliacao.tipo === '1' && estadosEspeciais[avaliacao.nota]"
                                :value="estadosEspeciais[avaliacao.nota]" severity="secondary" />
                            <Tag v-else-if="avaliacao.tipo === '1'" :class="defineClasses(avaliacao.nota)"
                                :value="nivelAtingido(avaliacao)?.label" />
                            <Tag v-else :value="avaliacao.nota" />
                        </div>

                        <div v-if="avaliacao.tipo === '1' && nivelAtingido(avaliacao)" class="text-sm text-color-secondary">
                            {{ nivelAtingido(avaliacao).texto }}
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
        </div>
    </div>
</template>

<style scoped>
.detalhes-aluno-container {
    max-width: 40rem;
    width: 100%;
}

.criterio-item:not(:last-child) {
    border-bottom: 1px solid var(--surface-border);
}

@media (max-width: 768px) {
    .detalhes-aluno-container {
        padding-top: 1rem;
    }
}
</style>
